import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/AuthContext";
import { useStore } from "context/StoreContext";
import { Link, Navigate } from "react-router-dom";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import { getLogoUrl } from "utils/logoUtils";
import { PasswordInput } from "components/ui";

const Register = () => {
 const { t } = useTranslation();
 const [name, setName] = useState("");
 const [username, setUsername] = useState("");
 const [email, setEmail] = useState("");
 const [password, setPassword] = useState("");
 const [confirmPassword, setConfirmPassword] = useState("");
 const [error, setError] = useState("");
 const [passwordError, setPasswordError] = useState("");
 const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
 const [acceptMarketingEmails, setAcceptMarketingEmails] = useState(false);
 const { register, userInfo: user } = useAuth();
 const { store } = useStore();
 const googleReCaptcha = useGoogleReCaptcha();

 const handleRegister = async (e: React.FormEvent) => {
  e.preventDefault();
  setError("");
  setPasswordError("");

  // Validate confirm password is filled
  if (!confirmPassword) {
   setPasswordError(t("validation.confirm_password_required") || "Please confirm your password");
   return;
  }

  // Validate passwords match
  if (password !== confirmPassword) {
   setPasswordError(t("validation.passwords_mismatch") || "Passwords do not match");
   return;
  }

  // Validate privacy policy acceptance (required for GDPR)
  if (!acceptPrivacyPolicy) {
   setError(t("validation.privacy_policy_required") || "You must accept the privacy policy to create an account");
   return;
  }

  // In test mode (Cypress), bypass reCAPTCHA
  const isTestMode = typeof window !== "undefined" && (window as any).Cypress;
  let captchaValue = "";
  
  if (isTestMode) {
   captchaValue = "test-token-cypress";
  } else {
   captchaValue = await (googleReCaptcha as any)?.executeV3("action");
   if (!captchaValue) {
    setError(t("auth.completeCaptcha"));
    return;
   }
  }
  try {
   // Register the user (backend will record consent during registration)
   await register({
    name,
    username,
    email,
    password,
    confirmPassword,
    captchaValue: captchaValue,
    acceptMarketingEmails: acceptMarketingEmails,
   });
  } catch (err) {
   setError(
    t(
     (err as any)?.response?.data?.errors?.[0]?.msg ||
      (err as any)?.response?.data?.message ||
      "invalid_credentials"
    )
   );
  }
 };

 return (
  <div
   className="flex items-center justify-center min-h-screen py-12"
   style={{
    background: "linear-gradient(135deg, #F5F1ED 0%, #EBE5DE 100%)",
   }}
  >
   {user && <Navigate to="/" replace={true} />}
   <div className="w-full max-w-md">
    {/* Header */}
    <div className="text-center mb-8">
     {store?.logo && (
      <div className="mb-4 flex justify-center">
       <img
        src={getLogoUrl(store.logo)}
        alt={store.name || "Store logo"}
        className="h-16 w-auto object-contain"
       />
      </div>
     )}
     <h1 className="text-4xl font-bold text-store-primary mb-2 font-store">
      {store?.name || t("common.appName")}
     </h1>
     {!store && (
      <p className="text-store-accent text-sm tracking-widest">
       EMPOWER YOUR COMMERCE
      </p>
     )}
    </div>

    {/* Form Card */}
    <div className="bg-white overflow-hidden">
     <form onSubmit={handleRegister} className="p-8 space-y-5">
      <div>
       <h2 className="text-2xl font-bold text-store-primary mb-1">
        {t("register.title")}
       </h2>
       <p className="text-store-primary-light text-sm">
        {t("auth.joinCommunity")}
       </p>
      </div>

      {error && (
       <div className="bg-red-50 border border-red-200 p-4">
        <p className="text-red-700 text-sm font-medium">{error}</p>
       </div>
      )}

      <div>
       <label className="block text-sm font-semibold text-store-primary mb-2">
        {t("register.name")}
       </label>
       <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t("register.name")}
        className="w-full px-4 py-3 border border-store-secondary focus:outline-none focus:border-store-secondary focus:ring-2 focus:ring-store-secondary focus:ring-opacity-20 transition-all"
       />
      </div>

      <div>
       <label className="block text-sm font-semibold text-store-primary mb-2">
        {t("auth.username")}
       </label>
       <input
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder={t("auth.username")}
        className="w-full px-4 py-3 border border-store-secondary focus:outline-none focus:border-store-secondary focus:ring-2 focus:ring-store-secondary focus:ring-opacity-20 transition-all"
       />
      </div>

      <div>
       <label className="block text-sm font-semibold text-store-primary mb-2">
        {t("auth.email")}
       </label>
       <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("auth.email")}
        className="w-full px-4 py-3 border border-store-secondary focus:outline-none focus:border-store-secondary focus:ring-2 focus:ring-store-secondary focus:ring-opacity-20 transition-all"
       />
      </div>

      <div>
       <PasswordInput
        label={t("auth.password")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t("auth.password")}
        className="w-full"
       />
      </div>

      <div>
       <PasswordInput
        label={t("register.confirm_password")}
        value={confirmPassword}
        onChange={(e) => {
         setConfirmPassword(e.target.value);
         // Clear error when user starts typing
         if (passwordError) {
          setPasswordError("");
         }
        }}
        placeholder={t("register.confirm_password")}
        error={passwordError}
        className="w-full"
        required
       />
      </div>

      {/* GDPR Consent Checkboxes */}
      <div className="space-y-3 pt-2">
       <div className="flex items-start">
        <input
         type="checkbox"
         id="privacy-policy"
         checked={acceptPrivacyPolicy}
         onChange={(e) => setAcceptPrivacyPolicy(e.target.checked)}
         required
         className="mt-1 mr-2 h-4 w-4 text-store-primary focus:ring-store-secondary border-store-secondary rounded"
        />
        <label htmlFor="privacy-policy" className="text-sm text-store-primary">
         {t("register.accept_privacy_policy") || "I accept the "}
         <Link
          to="/pages/privacy"
          target="_blank"
          className="text-store-secondary hover:text-store-secondary-dark underline"
         >
          {t("register.privacy_policy")}
         </Link>
         {" *"}
        </label>
       </div>

       <div className="flex items-start">
        <input
         type="checkbox"
         id="marketing-emails"
         checked={acceptMarketingEmails}
         onChange={(e) => setAcceptMarketingEmails(e.target.checked)}
         className="mt-1 mr-2 h-4 w-4 text-store-primary focus:ring-store-secondary border-store-secondary rounded"
        />
        <label htmlFor="marketing-emails" className="text-sm text-store-primary">
         {t("register.accept_marketing_emails") || "I agree to receive marketing emails (optional)"}
        </label>
       </div>
      </div>

      <button
       type="submit"
       className="w-full bg-store-primary text-white font-semibold py-3 hover:bg-store-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
       disabled={!acceptPrivacyPolicy}
      >
       {t("auth.register")}
      </button>
     </form>

     {/* Divider */}
     <div className="px-8 py-4 bg-store-background border-t border-store-secondary">
      <p className="text-center text-sm text-store-primary-light">
       {t("auth.already_have_account")}{" "}
       <Link
        to="/login"
        className="font-semibold text-store-secondary hover:text-store-secondary-dark transition-colors"
       >
        {t("auth.login_here")}
       </Link>
      </p>
     </div>
    </div>

    {/* Footer Note */}
    <p className="text-center text-xs text-store-primary-light mt-6">
     {t("auth.reCaptchaNote")}
    </p>
   </div>
  </div>
 );
};

export default Register;
